import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState } from 'react';

const team = [
  {
    name: 'Peter N. Stoll Jr.',
    title: 'Principal Agent',
    email: 'PStolljr@StollAgency.com',
    image: 'https://static.readdy.ai/image/878aa561146e1949d21d2a461d90b0e2/530099dc4f4ca8f801b8e4d8baca151e.png',
  },
  {
    name: 'Andrew Stoll',
    title: 'Insurance Agent',
    email: 'AndrewStoll@StollAgency.com',
    image: 'https://static.readdy.ai/image/878aa561146e1949d21d2a461d90b0e2/6e028986d6dbcb2d4feaa10fc8e54b3f.webp',
  },
  {
    name: 'Eric Stoll',
    title: 'Insurance Agent',
    email: 'EricStoll@StollAgency.com',
    image: 'https://static.readdy.ai/image/878aa561146e1949d21d2a461d90b0e2/6bea991b72d3b738cff0a8dd4b2697b9.webp',
  },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));
      const response = await fetch('https://readdy.ai/api/form/d6q2o9ma739gopmlh350', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-green-500 text-xs font-semibold tracking-widest uppercase mb-3">Bucks County &amp; Montgomery County, PA</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Contact Us</h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Reach out for a personalized insurance or surety bonding quote across Pennsylvania.
          </p>
        </div>
      </section>

      {/* Team Cards */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">Meet Our Team</h2>
            <p className="text-slate-400 text-sm">Reach any of our agents directly by email.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            {team.map((person) => (
              <div
                key={person.email}
                className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden flex flex-col items-center text-center hover:border-green-500/40 transition-all duration-300 group w-full max-w-xs"
              >
                <div className="w-full aspect-square overflow-hidden">
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 w-full">
                  <h3 className="text-white font-bold text-base mb-0.5">{person.name}</h3>
                  <p className="text-green-500 text-xs font-medium mb-3">{person.title}</p>
                  <a
                    href={`mailto:${person.email}`}
                    className="inline-flex items-center gap-1.5 text-slate-400 hover:text-green-500 text-xs transition-colors break-all"
                  >
                    <i className="ri-mail-line flex-shrink-0"></i>
                    <span>{person.email}</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info + Map */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

            {/* Left: Office Info */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-white">Our Office</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Address */}
                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-line text-lg text-green-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-1">Location</h3>
                    <a
                      href="https://maps.google.com/?q=1600+Horizon+Drive+Suite+112+Chalfont+PA+18914"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-400 text-sm leading-relaxed hover:text-green-500 transition-colors"
                    >
                      1600 Horizon Drive, Suite 112<br />
                      Chalfont, PA 18914
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-lg text-green-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-1">Phone &amp; Fax</h3>
                    <p className="text-sm">
                      <a href="tel:2158222427" className="text-green-500 hover:text-green-400 transition-colors">215-822-2427</a>
                    </p>
                    <p className="text-slate-400 text-sm">Fax: 215-822-7953</p>
                  </div>
                </div>

                {/* Hours */}
                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-lg text-green-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-1">Hours</h3>
                    <p className="text-slate-400 text-sm">Mon – Fri</p>
                    <p className="text-slate-400 text-sm">9:00 AM – 5:00 PM</p>
                  </div>
                </div>

                {/* Payment */}
                <div className="bg-slate-800 border border-slate-700 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-secure-payment-line text-lg text-green-500"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-1">Online Payment</h3>
                    <p className="text-slate-400 text-xs mb-3">Secure payments via our portal.</p>
                    <a
                      href="https://payments.myappliedproducts.com/flow/hpp"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-full text-xs font-semibold hover:bg-green-500 transition-all whitespace-nowrap cursor-pointer"
                    >
                      Pay Now <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Map */}
            <div className="rounded-2xl overflow-hidden border border-slate-700 shadow-xl" style={{ height: '340px' }}>
              <iframe
                title="Stoll Agency Office — Chalfont, Bucks County PA"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3046.3!2d-75.2097!3d40.2887!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6b1e1e1e1e1e1%3A0x1!2s1600+Horizon+Dr+Suite+112%2C+Chalfont%2C+PA+18914!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-4xl font-bold text-white mb-3">Request a Quote</h2>
              <p className="text-slate-400 text-sm">
                Serving contractors across <strong className="text-white">Bucks County</strong>, <strong className="text-white">Montgomery County</strong>, and all of Pennsylvania.
              </p>
            </div>

            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-2xl">
              <form onSubmit={handleSubmit} data-readdy-form id="contact-quote-form">
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-slate-300 mb-2">Full Name *</label>
                      <input
                        type="text" id="name" name="name"
                        value={formData.name} onChange={handleChange} required
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm placeholder-slate-500"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-300 mb-2">Email Address *</label>
                      <input
                        type="email" id="email" name="email"
                        value={formData.email} onChange={handleChange} required
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm placeholder-slate-500"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-slate-300 mb-2">Phone Number</label>
                      <input
                        type="tel" id="phone" name="phone"
                        value={formData.phone} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm placeholder-slate-500"
                        placeholder="(215) 555-1234"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-slate-300 mb-2">Company Name</label>
                      <input
                        type="text" id="company" name="company"
                        value={formData.company} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm placeholder-slate-500"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-300 mb-2">Message *</label>
                    <textarea
                      id="message" name="message"
                      value={formData.message} onChange={handleChange}
                      required maxLength={500} rows={5}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none text-sm placeholder-slate-500"
                      placeholder="Tell us about your insurance and bonding needs..."
                    ></textarea>
                    <p className="text-xs text-slate-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <button
                    type="submit" disabled={isSubmitting}
                    className="w-full px-6 py-4 bg-green-600 text-white rounded-full font-semibold hover:bg-green-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-900/50 border border-green-700 rounded-lg">
                      <p className="text-green-300 text-sm font-medium">Thank you! Your message has been sent. We'll be in touch soon.</p>
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg">
                      <p className="text-red-300 text-sm font-medium">Sorry, there was an error. Please try again or call us at 215-822-2427.</p>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
